import moment from "moment";

const DATE_TIME_FORMATS = [
  "YYYY-MM-DD HH:mm:ss",
  "YYYY-MM-DD HH:mm",
  "YYYY-MM-DDTHH:mm:ss",
  "YYYY-MM-DDTHH:mm",
];

const asMinutes = (hour, minute, period) => {
  const normalizedHour = hour % 12 + (period.toUpperCase() === "PM" ? 12 : 0);
  return normalizedHour * 60 + minute;
};

export const parseRosterTime = (value, { allowLegacy24h = false } = {}) => {
  if (typeof value === "number" && Number.isInteger(value) && value >= 0 && value < 24 * 60) {
    return value;
  }
  if (!value || typeof value !== "string") return null;

  const text = value.trim();
  const twelveHour = text.match(/^(0?[1-9]|1[0-2]):([0-5]\d)(?::([0-5]\d))?\s*([aApP][mM])$/);
  if (twelveHour) {
    if (Number(twelveHour[3] || 0) !== 0) return null;
    return asMinutes(Number(twelveHour[1]), Number(twelveHour[2]), twelveHour[4]);
  }
  if (allowLegacy24h) {
    const legacy = text.match(/^([01]?\d|2[0-3]):([0-5]\d)(?::([0-5]\d)(?:\.\d+)?)?$/);
    if (legacy) {
      const seconds = Number(legacy[3] || 0);
      return seconds === 0 ? Number(legacy[1]) * 60 + Number(legacy[2]) : null;
    }
  }
  return null;
};

export const formatRosterTime = (value) => {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "number") {
    const minutes = parseRosterTime(value);
    if (minutes === null) return "";
    const hour = Math.floor(minutes / 60);
    return `${hour % 12 || 12}:${String(minutes % 60).padStart(2, "0")} ${hour < 12 ? "AM" : "PM"}`;
  }

  const text = String(value).trim();
  const directMinutes = parseRosterTime(text, { allowLegacy24h: true });
  if (directMinutes !== null) return formatRosterTime(directMinutes);

  const parsed = moment(text, [...DATE_TIME_FORMATS, moment.ISO_8601], true);
  return parsed.isValid() ? formatRosterTime(parsed.hours() * 60 + parsed.minutes()) : "";
};

const formatRosterField = (primaryValue, fallbackValue) => {
  return formatRosterTime(primaryValue) || formatRosterTime(fallbackValue);
};

export const formatRosterRange = (roster = {}) => {
  const start = formatRosterField(roster.start_time, roster.start_datetime);
  const end = formatRosterField(roster.end_time, roster.end_datetime);
  return start && end ? `${start} - ${end}` : "";
};

export const normalizeRosterTime = (value) => {
  const minutes = parseRosterTime(value);
  return minutes === null ? "" : formatRosterTime(minutes);
};

export const normalizePickerTime24Hour = (value) => {
  const minutes = parseRosterTime(value, { allowLegacy24h: true });
  if (minutes === null) return "";
  return `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
};

export const rosterTimeOptions = (slotMinutes = 15, startMinutes = 0, endMinutes = 24 * 60) => {
  const cadence = Number(slotMinutes);
  if (!Number.isInteger(cadence) || cadence <= 0) return [];
  if (!Number.isInteger(startMinutes) || !Number.isInteger(endMinutes) || startMinutes < 0 || endMinutes > 24 * 60 || startMinutes >= endMinutes || startMinutes % cadence !== 0) return [];
  return Array.from({ length: Math.ceil((endMinutes - startMinutes) / cadence) }, (_, index) => {
    const minutes = startMinutes + index * cadence;
    return minutes < endMinutes ? { id: formatRosterTime(minutes), value: formatRosterTime(minutes) } : null;
  }).filter(Boolean);
};

const parseBoundary = (value) => {
  if (!value) return null;
  const direct = parseRosterTime(String(value), { allowLegacy24h: true });
  if (direct !== null) return direct;
  const parsed = moment(String(value), [...DATE_TIME_FORMATS, moment.ISO_8601], true);
  return parsed.isValid() ? parsed.hours() * 60 + parsed.minutes() : null;
};

export const rosterTimeOptionsBetween = (businessStart, businessEnd, slotMinutes = 15) => {
  const start = parseBoundary(businessStart);
  const end = parseBoundary(businessEnd);
  if (start === null || end === null || end <= start) return [];
  return rosterTimeOptions(slotMinutes).filter((option) => {
    const minutes = parseRosterTime(option.value);
    return minutes >= start && minutes <= end;
  });
};

export const isRosterTimeAfter = (start, end) => {
  const startMinutes = parseRosterTime(start, { allowLegacy24h: true });
  const endMinutes = parseRosterTime(end, { allowLegacy24h: true });
  return startMinutes !== null && endMinutes !== null && endMinutes > startMinutes;
};
